// Backlog 099 Phase 2 — beautified terminal-state renderer.
//
// Pure module: input = the polled DeployTrace + the entity slug; output = a
// chalk-formatted multi-line string with a one-line headline and a short
// remediation hint citing files, flags, or backlog items where applicable.
//
// The classifier reads the failed step + the underlying error text the API
// surfaces (visible to us after PR #304's 091-L1 fix) and dispatches to the
// right per-class formatter. Default fallback echoes the verbatim error and
// the trace_id so a developer always has *something* actionable, even on
// failure modes the renderer hasn't learned to recognise yet.
//
// §23 voice rules apply across every variant: plain, no exclamations, no
// emoji, no stack traces in the user-facing surface. Raw trace + stack stay
// available via `grid follow <trace_id> --verbose`.

import chalk from 'chalk';
import type { DeployTrace } from '../services/plug-poll.js';
import type { Step } from './multi-step.js';

// ──────────────────────────────────────────────────────────────────────────
// Public entry point
// ──────────────────────────────────────────────────────────────────────────

export function renderTerminalState(
  trace: DeployTrace,
  entitySlug: string,
): string {
  const failed = findFailedStep(trace);
  const traceId = trace.trace_id;

  const block = classify(trace, failed, entitySlug);
  const lines: string[] = [];

  lines.push(chalk.red(block.headline));
  lines.push('');
  for (const para of block.body) {
    lines.push(`  ${para}`);
  }
  lines.push('');
  lines.push(
    chalk.dim(
      `  Trace ${traceId}. Run \`grid follow ${traceId} --verbose\` for the full step log.`,
    ),
  );

  return lines.join('\n');
}

// ──────────────────────────────────────────────────────────────────────────
// Classifier
// ──────────────────────────────────────────────────────────────────────────

interface RenderedBlock {
  headline: string;
  body: string[];
}

function classify(
  trace: DeployTrace,
  failed: Step | null,
  entitySlug: string,
): RenderedBlock {
  const errorCode = trace.error?.code ?? null;
  const stepName = failed?.step ?? trace.error?.failed_step ?? null;
  const stepCode = typeof failed?.code === 'string' ? failed.code : null;
  const errorText = (failed?.error ?? trace.error?.message ?? '').toString();
  const lower = errorText.toLowerCase();

  // 1. cloud_build + npm ci lockfile mismatch
  if (
    stepName === 'cloud_build' &&
    (lower.includes('lock file') ||
      lower.includes('lockfile') ||
      (lower.includes('npm ci') && lower.includes('out of sync')) ||
      (lower.includes('npm ci') && lower.includes('package-lock')))
  ) {
    return {
      headline: 'Build failed: package-lock.json is out of sync with package.json.',
      body: [
        'Run `npm install` locally and commit the regenerated lock file, then re-plug.',
        'Backlog 097 (live) auto-recovers this server-side; if you see this, the recovery itself failed.',
      ],
    };
  }

  // 2. cloud_build + TypeScript / ESLint compile error
  if (
    stepName === 'cloud_build' &&
    (lower.includes('typescript') ||
      /error\s+ts\d+/i.test(errorText) ||
      /\.tsx?:\d+:\d+/.test(errorText))
  ) {
    const fileLine = extractTsFileLine(errorText);
    const where = fileLine
      ? `Build failed: TypeScript error at ${fileLine}.`
      : 'Build failed: TypeScript compile error.';
    return {
      headline: where,
      body: [
        'Fix locally and re-plug. `next dev` does not full-type-check; `next build` does.',
        'Backlog 015-B (when shipped) catches this client-side before the upload.',
      ],
    };
  }

  // 3. wait_rollout + probe 404 (or PROBE_FAILED with /health hint)
  if (
    stepName === 'wait_rollout' &&
    (stepCode === 'PROBE_FAILED' ||
      (lower.includes('probe') && lower.includes('404')) ||
      (lower.includes('/health') && lower.includes('404')))
  ) {
    return {
      headline:
        "Rollout failed: pod is running but the platform's startup probe at `/health` returns 404.",
      body: [
        'Add a `/health` route on the affected service, or use the platform default TCP probe (now live since #308).',
        'If you see this on a service that does not declare `health:`, your yaml may carry a stale `health:` field — remove it.',
      ],
    };
  }

  // 4. wait_rollout + CrashLoopBackOff
  if (stepName === 'wait_rollout' && stepCode === 'RUNTIME_CRASHLOOP') {
    const restart = extractRestartCount(failed);
    const headline =
      restart != null
        ? `Rollout failed: pod started, then exited ${restart} times.`
        : 'Rollout failed: pod started, then crashed repeatedly.';
    return {
      headline,
      body: [
        'Inspect logs: `grid logs <entity> --tail`.',
        'Common causes: missing env var, port mismatch, dependency error on startup.',
      ],
    };
  }

  // 5. apply_manifests + Ingress conflict (legacy_pre_cutover)
  if (
    stepName === 'apply_manifests' &&
    (lower.includes('already managed by ingress') ||
      lower.includes('hostname') && lower.includes('already') ||
      lower.includes('legacy_pre_cutover'))
  ) {
    return {
      headline: 'Deploy failed: hostname already claimed by legacy namespace.',
      body: [
        'Pre-010 entity. Run:',
        chalk.cyan(`    grid plug --migrate-from-legacy <legacy-namespace>`),
        'Recipe: docs/operations/2026-05-07-legacy-pre-cutover-migration.md (per backlog 091).',
      ],
    };
  }

  // 6. apply_manifests + namespace not found / RBAC forbidden
  if (
    stepName === 'apply_manifests' &&
    (lower.includes('namespaces') && lower.includes('not found') ||
      lower.includes('namespace') && lower.includes('not found') ||
      lower.includes('forbidden') ||
      lower.includes('rbac'))
  ) {
    const ns = extractNamespace(errorText) ?? trace.entity?.namespace ?? '<unknown>';
    return {
      headline: `Deploy failed: cannot apply manifests to namespace ${ns}.`,
      body: [
        `Check that the entity ${entitySlug} is properly registered (run \`grid get entities\` to verify it shows up).`,
        'If it does, this is a platform bug — paste the trace id below into a feedback report.',
      ],
    };
  }

  // 7. INGRESS_MISSING (backlog 084)
  if (
    errorCode === 'INGRESS_MISSING' ||
    stepCode === 'INGRESS_MISSING'
  ) {
    return {
      headline:
        'Deploy failed: no Ingress was rendered for this entity.',
      body: [
        'Check `cloudgrid.yaml`: every `app` needs at least one service with `path:`,',
        'or declare `expose: false` for an internal-only entity.',
        'Per backlog 082, `grid plug` should have caught this client-side. Open a bug if you see this in production.',
      ],
    };
  }

  // ── Default fallback ───────────────────────────────────────────────────
  const verbatim = errorText || trace.error?.message || 'unknown failure';
  const where = stepName ? ` at step \`${stepName}\`` : '';
  return {
    headline: `Deploy failed${where}.`,
    body: [
      `The API said: ${verbatim}`,
      `For the full step log, run \`grid follow ${trace.trace_id} --verbose\`.`,
    ],
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function findFailedStep(trace: DeployTrace): Step | null {
  const failedStepName = trace.error?.failed_step;
  if (failedStepName) {
    const named = trace.steps.find(
      (s) => s.step === failedStepName && s.status === 'failed',
    );
    if (named) return named;
  }
  return trace.steps.find((s) => s.status === 'failed') ?? null;
}

function extractTsFileLine(error: string): string | null {
  // "src/components/Header.tsx:42:7 - error TS2322: ..."
  const m = error.match(/([\w./@-]+\.tsx?):(\d+)(?::\d+)?/);
  if (!m) return null;
  return `${m[1]}:${m[2]}`;
}

function extractRestartCount(step: Step | null): number | null {
  if (!step) return null;
  const details = step.details as Record<string, unknown> | undefined;
  if (details && typeof details.restart_count === 'number') {
    return details.restart_count;
  }
  return null;
}

function extractNamespace(error: string): string | null {
  // "namespaces \"foo-bar\" not found" or "namespace foo-bar not found".
  // Real K8s namespaces are DNS-1123 (no underscores), but the regex stays
  // permissive so that test fixtures and odd-shaped error strings still
  // parse cleanly rather than dropping back to the entity.namespace fallback.
  const m =
    error.match(/namespaces?\s+"([\w-]+)"/) ??
    error.match(/namespace\s+([\w-]+)/);
  return m ? m[1] : null;
}
